<template>
    <div class="flex flex-col gap-2">
        <UInputMenu
            v-model="picker"
            :search="search"
            :search-attributes="['name']"
            placeholder="Search partners..."
            option-attribute="name"
            by="id"
            nullable
            size="lg"
            @update:model-value="onPick"
        />
        <div v-if="selectedList.length" class="flex flex-col gap-1">
            <div
                v-for="p in selectedList"
                :key="p.id"
                class="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-2.5 py-1.5"
            >
                <span class="truncate text-sm text-gray-700" :title="p.name">{{ p.name }}</span>
                <UButton icon="i-ph-x-bold" size="2xs" color="gray" variant="ghost" :padded="false" class="shrink-0 p-0.5" @click="removeOne(p.id)" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Filter } from '~/types/filter'

interface PartnerRef { id: string; name: string }

const emit = defineEmits<{ 'update:filter': [Filter] }>()
const props = defineProps<{ filter: Filter }>()
const client = useSupabaseClient()

const picker = ref<any>(null)
const ids = computed<string[]>(() => (Array.isArray(props.filter.value) ? props.filter.value : []))
const selectedList = computed<PartnerRef[]>(() => (Array.isArray(props.filter.selected) ? props.filter.selected : []))

onMounted(async () => {
    if (ids.value.length && selectedList.value.length !== ids.value.length) {
        const { data } = await client.from('partners').select('id, name').in('id', ids.value)
        if (data?.length) emitSelected(data as PartnerRef[])
    }
})

function emitSelected(list: PartnerRef[]) {
    emit('update:filter', {
        ...props.filter,
        selected: list.length ? list : undefined,
        value: list.length ? list.map(p => p.id) : undefined,
    })
}

function onPick(partner: PartnerRef | null) {
    if (!partner) return
    if (!selectedList.value.some(x => x.id === partner.id)) {
        emitSelected([...selectedList.value, { id: partner.id, name: partner.name }])
    }
    nextTick(() => { picker.value = null })
}

function removeOne(id: string) {
    emitSelected(selectedList.value.filter(x => x.id !== id))
}

async function search(q: string) {
    const { data } = await client.from('partners')
        .select('id, name')
        .is('deleted_at', null)
        .ilike('name', `%${q ?? ''}%`)
        .order('name')
        .limit(10)
    return (data ?? []).filter(r => !selectedList.value.some(s => s.id === r.id))
}
</script>
